import React, { useEffect, useMemo, useRef } from 'react'
import { useHospitalSelection } from '../../hooks/useHospitalSelection'
import { useHospitalData } from '../../hooks/useHospitalData'
import { usePersistedState } from '../../hooks/usePersistedState'
import DateRangeSlider from '../../components/DateRangeSlider/DateRangeSlider'
import LoadingNotice from '../../components/LoadingNotice/LoadingNotice'
import NoDataOverlay from '../../components/Common/NoDataOverlay'
import StackedBarChart from '../../components/Charts/StackedBarChart'
import { parseMonthToValue } from '../../utils/dataUtils'
import { MonthInfo, generateMonthList } from '../../utils/coreUtils'
import { createChartExportHandlers, createMenuToggle, setupMenuClickOutside, exportToCSV } from '../../utils/exportUtils'
import './HuddleBoardTeam.css'

interface ZohoResponse {
    response?: {
        result?: {
            column_order?: string[]
            rows?: any[][]
        }
    }
}

const Q4_QUESTION = 'Was each person introduced by name and role at the beginning of the huddle?'
const Q5_QUESTION = 'Were the team members names & roles written on the board, either during this huddle or previously?'

const ANSWER_ORDER: string[] = ['Yes, all', 'No, only some', 'No, none']

const LEGEND_ORDER: string[] = ['No, none', 'No, only some', 'Yes, all']

const ANSWER_COLORS: Record<string, string> = {
    'Yes, all': '#2C5985',
    'No, only some': '#AEDCD5',
    'No, none': '#E48E43',
}

const DATA_LABEL_COLORS: Record<string, string> = {
    'Yes, all': '#ffffff',
    'No, only some': '#626262',
    'No, none': '#ffffff',
}

const SECTION_MENU_ID = 'hdl-board-team-section-export-menu'
const HDL_SLIDER_KEY = 'hdl_slider'
const HDL_LAST_HOSPITAL_COMBO_KEY = 'hdl_last_hospital_combo'

const getSliderIndices = (monthList: MonthInfo[], range: [number, number]): [number, number] => {
    if (!monthList.length) return [0, 0]
    const maxIndex = monthList.length - 1
    const [startVal, endVal] = range
    if (!startVal || !endVal) return [0, maxIndex]

    let startIdx = monthList.findIndex(m => m.value === startVal)
    if (startIdx === -1) startIdx = monthList.findIndex(m => m.value >= startVal)
    if (startIdx === -1) startIdx = 0

    let endIdx = -1
    for (let i = maxIndex; i >= 0; i--) {
        if (monthList[i].value <= endVal) { endIdx = i; break }
    }
    if (endIdx === -1) endIdx = maxIndex

    startIdx = Math.max(0, Math.min(startIdx, maxIndex))
    endIdx = Math.max(0, Math.min(endIdx, maxIndex))
    if (startIdx > endIdx) return [0, maxIndex]
    return [startIdx, endIdx]
}

const normalizeAnswer = (raw: string): string | null => {
    const trimmed = raw.trim()
    if (!trimmed) return null
    const lower = trimmed.toLowerCase().replace(/\s+/g, '')
    if (lower.startsWith('yes')) return 'Yes, all'
    if (lower.startsWith('no,onlysome') || lower.startsWith('no,only')) return 'No, only some'
    if (lower.startsWith('no,none') || lower === 'none') return 'No, none'
    return null
}

const HuddleBoardTeam: React.FC = () => {
    const { selectedHospitalIds, isMultiSelect } = useHospitalSelection()

    const hospitalIds = useMemo(
        () => (Array.isArray(selectedHospitalIds) ? selectedHospitalIds : []),
        [selectedHospitalIds]
    )

    const { data, loading, initialLoading, error } = useHospitalData<ZohoResponse>(
        '/api/hdl/board_team_introduced_q4',
        hospitalIds
    )

    const {
        data: q5Data,
        loading: q5Loading,
        initialLoading: q5InitialLoading,
        error: q5Error,
    } = useHospitalData<ZohoResponse>(
        '/api/hdl/board_team_names_q5',
        hospitalIds
    )

    const sectionRef = useRef<HTMLDivElement>(null)
    const sectionExports = useMemo(
        () => createChartExportHandlers(
            sectionRef,
            'Discussion & Board Use: Team — Q4 & Q5',
            'huddle_board_team_q4_q5',
            { fontBoost: true, exportTitle: 'Discussion & Board Use: Team', exportTitleAlign: 'center', exportTitleFontSize: '28px', extraHideSelectors: ['.stacked-bar-chart-legend'] }
        ),
        []
    )
    const toggleSectionMenu = useMemo(() => createMenuToggle(SECTION_MENU_ID), [])

    useEffect(() => {
        return setupMenuClickOutside(() => sectionExports.closeMenu(SECTION_MENU_ID))
    }, [sectionExports])

    const rows: any[][] = data?.response?.result?.rows || []
    const q5Rows: any[][] = q5Data?.response?.result?.rows || []

    const monthList: MonthInfo[] = useMemo(() => {
        const allRows = [...rows, ...q5Rows]
        if (!allRows.length) return []
        return generateMonthList(allRows, 2)
    }, [rows, q5Rows])

    const hospitalIdsKey = hospitalIds.join('_')
    const persistenceKey = `${HDL_SLIDER_KEY}_${hospitalIdsKey}`
    const [sliderRangeValues, setSliderRangeValues] = usePersistedState<[number, number]>(
        persistenceKey,
        [0, 0]
    )

    const userInteractedRef = useRef(false)
    const initSeenRef = useRef(false)
    const lastDetectedKeyRef = useRef<string | null>(null)

    if (hospitalIds.length > 0 && lastDetectedKeyRef.current !== persistenceKey) {
        const isFirstInit = !initSeenRef.current
        lastDetectedKeyRef.current = persistenceKey
        initSeenRef.current = true

        if (isFirstInit) {
            try {
                sessionStorage.setItem(HDL_LAST_HOSPITAL_COMBO_KEY, hospitalIdsKey)
            } catch {}
            userInteractedRef.current = sliderRangeValues[0] !== 0 && sliderRangeValues[1] !== 0
        } else {
            userInteractedRef.current = false
            try {
                sessionStorage.setItem(HDL_LAST_HOSPITAL_COMBO_KEY, hospitalIdsKey)
            } catch {}
        }
    }

    const isLoadingData = loading || q5Loading

    useEffect(() => {
        if (!monthList.length) return
        if (isLoadingData) return
        const maxIdx = monthList.length - 1
        const fullRange: [number, number] = [monthList[0].value, monthList[maxIdx].value]
        const [curStart, curEnd] = sliderRangeValues

        if (!userInteractedRef.current) {
            if (curStart !== fullRange[0] || curEnd !== fullRange[1]) {
                setSliderRangeValues(fullRange)
            }
            return
        }

        const stillValid =
            curStart >= monthList[0].value &&
            curEnd <= monthList[maxIdx].value &&
            curStart <= curEnd
        if (!curStart || !curEnd || !stillValid) {
            userInteractedRef.current = false
            setSliderRangeValues(fullRange)
        }
    }, [hospitalIdsKey, monthList, isLoadingData, sliderRangeValues, setSliderRangeValues])

    const [startIdx, endIdx] = useMemo(
        () => getSliderIndices(monthList, sliderRangeValues),
        [monthList, sliderRangeValues]
    )

    type Aggregated = {
        counts: Record<string, number>
        total: number
        nCount: number
        percentages: Record<string, number>
        hospitalCount: number
    }

    const aggregateRows = (sourceRows: any[][]): Aggregated => {
        const empty: Aggregated = { counts: {}, total: 0, nCount: 0, percentages: {}, hospitalCount: 0 }
        if (!monthList.length || !sourceRows.length) return empty

        const minVal = monthList[startIdx]?.value ?? 0
        const maxVal = monthList[endIdx]?.value ?? 0

        type HospEntry = {
            counts: Record<string, number>
            total: number
            nCount: number
        }
        const perHospital: Record<string, HospEntry> = {}
        const seenGroups = new Set<string>()

        sourceRows.forEach(r => {
            const hospId = String(r[0])
            const monthVal = parseMonthToValue(r[2])
            if (!monthVal || monthVal < minVal || monthVal > maxVal) return
            const answer = normalizeAnswer(String(r[3] || ''))
            if (!answer) return
            const numerator = Number(r[4]) || 0
            const denom = Number(r[5]) || 0
            const nCountVal = Number(r[7]) || 0

            if (!perHospital[hospId]) perHospital[hospId] = { counts: {}, total: 0, nCount: 0 }
            const h = perHospital[hospId]

            h.counts[answer] = (h.counts[answer] || 0) + numerator
            const gk = `${hospId}|${monthVal}`
            if (!seenGroups.has(gk)) {
                seenGroups.add(gk)
                h.total += denom
                if (denom > 0) h.nCount += nCountVal
            }
        })

        const contributing = Object.values(perHospital).filter(h => h.total > 0)
        if (contributing.length === 0) return empty

        const counts: Record<string, number> = {}
        let total = 0
        let nCount = 0
        for (const h of contributing) {
            for (const [a, c] of Object.entries(h.counts)) counts[a] = (counts[a] || 0) + c
            total += h.total
            nCount += h.nCount
        }

        const percentages: Record<string, number> = {}
        for (const a of ANSWER_ORDER) {
            percentages[a] = total > 0 ? ((counts[a] || 0) / total) * 100 : 0
        }

        return { counts, total, nCount, percentages, hospitalCount: contributing.length }
    }

    const q4Aggregated = useMemo(
        () => aggregateRows(rows),
        [rows, monthList, startIdx, endIdx, isMultiSelect]
    )
    const q5Aggregated = useMemo(
        () => aggregateRows(q5Rows),
        [q5Rows, monthList, startIdx, endIdx, isMultiSelect]
    )

    const q4HasData = q4Aggregated.hospitalCount > 0
    const q5HasData = q5Aggregated.hospitalCount > 0

    const roundedPercentages = (agg: Aggregated): Record<string, number> => {
        const result: Record<string, number> = {}
        if (agg.hospitalCount <= 0) {
            ANSWER_ORDER.forEach(a => { result[a] = 0 })
            return result
        }
        // Largest-remainder so displayed labels sum to exactly 100
        const rawPcts = ANSWER_ORDER.map(a => agg.percentages[a] || 0)
        const floors = rawPcts.map(p => Math.floor(p))
        const remainder = 100 - floors.reduce((s, f) => s + f, 0)
        const diffs = rawPcts.map((p, i) => ({ i, diff: p - floors[i] })).sort((a, b) => b.diff - a.diff)
        const adjusted = [...floors]
        for (let k = 0; k < remainder && k < diffs.length; k++) adjusted[diffs[k].i] += 1
        ANSWER_ORDER.forEach((a, i) => { result[a] = adjusted[i] })
        return result
    }

    const buildSeriesAndColors = (agg: Aggregated) => {
        const rounded = roundedPercentages(agg)
        const items = ANSWER_ORDER
            .map(answer => ({ name: answer, data: [rounded[answer]], color: ANSWER_COLORS[answer], labelColor: DATA_LABEL_COLORS[answer] }))
            .filter(item => item.data[0] > 0)
        return {
            series: items.map(({ name, data }) => ({ name, data })),
            colors: items.map(({ color }) => color),
            dataLabelColors: items.map(({ labelColor }) => labelColor),
        }
    }

    const q4Built = useMemo(() => buildSeriesAndColors(q4Aggregated), [q4Aggregated])
    const q5Built = useMemo(() => buildSeriesAndColors(q5Aggregated), [q5Aggregated])

    const handleDownloadCSV = () => {
        const hospitalIdsStr = hospitalIds.join(',')
        const startLabel = monthList[startIdx]?.label || ''
        const endLabel = monthList[endIdx]?.label || ''
        const monthRange = startLabel && endLabel ? `${startLabel} - ${endLabel}` : ''

        const buildBlock = (question: string, agg: Aggregated) => {
            const rounded = roundedPercentages(agg)
            const block: (string | number)[][] = ANSWER_ORDER.map(answer => {
                const count = agg.counts[answer] || 0
                const percent = agg.total > 0 ? `${rounded[answer]}%` : ''
                return [hospitalIdsStr, monthRange, question, answer, count, percent]
            })
            block.push([hospitalIdsStr, monthRange, question, 'Total (N)', agg.nCount || 0, ''])
            return block
        }
        const csvRows: (string | number)[][] = [
            ...buildBlock(Q4_QUESTION, q4Aggregated),
            ...buildBlock(Q5_QUESTION, q5Aggregated),
        ]
        exportToCSV({
            headers: ['Hospital ID(s)', 'Month Range', 'Question', 'Answer', 'Count', 'Percentage'],
            rows: csvRows,
            filename: 'huddle_board_team_q4_q5',
        })
    }

    const showInitialLoading = (initialLoading && loading) || (q5InitialLoading && q5Loading)
    const isRefreshing = (!initialLoading && loading) || (!q5InitialLoading && q5Loading)
    const hasHospitals = hospitalIds.length > 0
    const combinedError = error || q5Error
    const noData = !initialLoading && !loading && !q5InitialLoading && !q5Loading && !monthList.length

    return (
        <div className="hdl-board-team">
            <div className="hdl-page-header">
                <h1 className="hdl-page-title">Discussion & Board Use: Team</h1>
                <div className="hdl-slider-wrap">
                    {monthList.length > 0 && (
                        <DateRangeSlider
                            monthList={monthList}
                            startIndex={startIdx}
                            endIndex={endIdx}
                            onChange={(s, e) => {
                                const sv = monthList[s]?.value || 0
                                const ev = monthList[e]?.value || 0
                                if (sv && ev) {
                                    userInteractedRef.current = true
                                    setSliderRangeValues([sv, ev])
                                }
                            }}
                        />
                    )}
                </div>
            </div>

            <div className={`hdl-card${hasHospitals && !combinedError && noData ? ' hdl-card-empty' : ''}`}>
                {showInitialLoading && <LoadingNotice title="Loading huddle board team data…" />}

                {!hasHospitals && (
                    <div className="hdl-notice">Select at least one hospital to view data.</div>
                )}

                {hasHospitals && combinedError && (
                    <div className="hdl-notice hdl-notice-error">Failed to load data: {combinedError}</div>
                )}

                <NoDataOverlay visible={hasHospitals && !combinedError && noData} />

                {hasHospitals && !combinedError && !noData && (
                    <div className="hdl-board-team-section" ref={sectionRef}>
                        <div className="hdl-board-team-section-head-row">
                            <div className="hdl-board-team-section-title">Q4 &amp; Q5</div>
                            <div className="export-menu export-ignore">
                                <button className="hamburger" onClick={toggleSectionMenu}>&#9776;</button>
                                <div id={SECTION_MENU_ID} className="export-dropdown">
                                    <div onClick={() => sectionExports.handleDownloadSVG(SECTION_MENU_ID)}>Download SVG</div>
                                    <div onClick={() => sectionExports.handleDownloadPNG(SECTION_MENU_ID)}>Download PNG</div>
                                    <div onClick={() => { handleDownloadCSV(); sectionExports.closeMenu(SECTION_MENU_ID); }}>Download CSV</div>
                                </div>
                            </div>
                        </div>

                        <div className="hdl-board-team-charts">
                            <div className="hdl-board-team-question">{Q4_QUESTION}</div>
                            <div className="hdl-board-team-question">{Q5_QUESTION}</div>
                            <div className="hdl-board-team-legend-spacer" aria-hidden="true" />

                            {q4HasData ? (
                                <div className="hdl-board-team-chart-stack">
                                    <StackedBarChart
                                        categories={['']}
                                        series={q4Built.series}
                                        colors={q4Built.colors}
                                        height={480}
                                        columnWidth="100%"
                                        fontFamily="Poppins, 'Segoe UI', sans-serif"
                                        yAxisShow={false}
                                        xAxisShow={false}
                                        showGrid={false}
                                        sparkline={true}
                                        showDataLabels={true}
                                        dataLabelColors={q4Built.dataLabelColors}
                                        dataLabelMinPercent={8}
                                        fillOpacity={1}
                                        exportFilename="huddle_board_team_q4"
                                    />
                                </div>
                            ) : (
                                <div className="hdl-board-team-empty">No data for the selected range</div>
                            )}

                            {q5HasData ? (
                                <div className="hdl-board-team-chart-stack">
                                    <StackedBarChart
                                        categories={['']}
                                        series={q5Built.series}
                                        colors={q5Built.colors}
                                        height={480}
                                        columnWidth="100%"
                                        fontFamily="Poppins, 'Segoe UI', sans-serif"
                                        yAxisShow={false}
                                        xAxisShow={false}
                                        showGrid={false}
                                        sparkline={true}
                                        showDataLabels={true}
                                        dataLabelColors={q5Built.dataLabelColors}
                                        dataLabelMinPercent={8}
                                        fillOpacity={1}
                                        exportFilename="huddle_board_team_q5"
                                    />
                                </div>
                            ) : (
                                <div className="hdl-board-team-empty">No data for the selected range</div>
                            )}


                            <div className="hdl-board-team-legend">
                                {LEGEND_ORDER.map(answer => (
                                    <div key={answer} className="hdl-board-team-legend-item">
                                        <span
                                            className="hdl-board-team-legend-dot"
                                            style={{ backgroundColor: ANSWER_COLORS[answer] }}
                                        />
                                        <span className="hdl-board-team-legend-label">{answer}</span>
                                    </div>
                                ))}
                            </div>

                            {(q4HasData || q5HasData) && (
                                <div className="hdl-board-team-n-row">
                                    <span className="hdl-board-team-n-label">N=</span>
                                    <span className="hdl-board-team-n-val">{q4HasData ? q4Aggregated.nCount : ''}</span>
                                    <span className="hdl-board-team-n-val">{q5HasData ? q5Aggregated.nCount : ''}</span>
                                    <span aria-hidden="true" />
                                </div>
                            )}
                        </div>

                        {isRefreshing && (
                            <div className="chart-loading-overlay">
                                <div>Loading...</div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    )
}

export default HuddleBoardTeam
